import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useOkapiKy } from "@folio/stripes/core";
import { useQueryClient, useMutation } from "react-query";
import { Pane, Button, MenuSection, Modal, MessageBanner, ErrorModal, Layout, List, } from "@folio/stripes/components";
import { useIntl, FormattedMessage } from "react-intl";
import NavComponent from "../../components/NavComponent";
import TableComponent from "../../components/TableComponent";
import UserDetail from "./UserDetail";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { createStripesSource } from "../../utils/createStripesSource";
import { formatCountSubtitle } from "../../utils/paneCount";
import { useUserActions } from "../../hooks/useUserActions";
import { useUserTable } from "../../hooks/useUserTable";
import { useUserFilters } from "../../hooks/useUserFilters";
import { UserFilters } from "../../components/UserFilters";
import { useUsersData } from "../../hooks/useUsersData";
import { useListErrorMessage } from "../../hooks/useListErrorMessage";
import { usersEdit, EXTERNAL_ROUTES } from "../../types/routePaths";
import { updateUser } from "../../api/userService";
import { useActiveRoles, hasRole } from "../../utils/permissionsUtils";
import { RoleEnum } from "../../types/RoleEnum";
import { useModal } from "../../utils/useModal";
var UserManagementList = function () {
    var formatMessage = useIntl().formatMessage;
    var history = useHistory();
    var kyInstance = useOkapiKy();
    var queryClient = useQueryClient();
    var activeRoles = useActiveRoles();
    var isCurrentUserAdmin = hasRole(activeRoles, RoleEnum.ADMIN_LEGAL_DEPOSITS);
    var _a = useState(true), filterPaneIsVisible = _a[0], setFilterPaneIsVisible = _a[1];
    var _b = useState(false), showCreateUserModal = _b[0], setShowCreateUserModal = _b[1];
    var _c = useModal(), isErrorModalOpen = _c.isOpen, errorModalMessage = _c.message, showErrorModal = _c.openModal, hideErrorModal = _c.closeModal;
    var handleEditUser = function (userId) {
        history.push(usersEdit(userId));
    };
    var filtersHook = useUserFilters();
    var dataHook = useUsersData(filtersHook.appliedFilters);
    var errorMessage = useListErrorMessage(dataHook.error);
    var actionsHook = useUserActions();
    var columns = useUserTable(actionsHook.handleRowClick).columns;
    var toggleFilterPane = function () {
        return setFilterPaneIsVisible(function (previousState) { return !previousState; });
    };
    var source = createStripesSource({
        ky: null,
        data: dataHook.usersData,
        totalRecords: dataHook.totalRecords,
        isLoading: dataHook.isLoading,
        isError: dataHook.isError,
        errorMessage: errorMessage,
        isLoaded: true,
        fetchMore: function () {
            return dataHook.fetchMoreData(dataHook.pageOffset + dataHook.pageSize);
        },
    });
    var setStatusMutation = useMutation({
        mutationFn: function (_a) {
            var userId = _a.userId, status = _a.status;
            return updateUser(kyInstance, userId, { status: status });
        },
        onSuccess: function () {
            queryClient.invalidateQueries({ queryKey: ["users"] });
            queryClient.invalidateQueries({ queryKey: ["user-detail"] });
        },
        onError: function () {
            showErrorModal(formatMessage({ id: "ui-br-legaldeposits.users.error.statusChange" }));
        },
    });
    return (React.createElement(RecekPaneSet, null,
        React.createElement(ErrorModal, { open: isErrorModalOpen, label: formatMessage({ id: "ui-br-legaldeposits.modal.error.label" }), content: errorModalMessage, onClose: hideErrorModal }),
        filterPaneIsVisible && (React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.pane.title.navigation",
            }) },
            React.createElement(Layout, { className: "display-flex flex-direction-column" },
                React.createElement(NavComponent, null),
                React.createElement(Button, { buttonStyle: "none", id: "clickable-reset-all", disabled: !filtersHook.isAnyFilterActive(), marginBottom0: true, onClick: filtersHook.handleClearFilters },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.resetAll" })),
                React.createElement(UserFilters, { filters: filtersHook.filters, onFilterChange: filtersHook.handleFilterChange, onStatusChange: filtersHook.handleStatusChange, onRoleChange: filtersHook.handleRoleChange, onApplyFilters: filtersHook.handleApplyFilters, hasUnappliedChanges: filtersHook.hasUnappliedChanges() })))),
        React.createElement(Pane, { id: "contentPane", defaultWidth: "fill", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.navigation.users",
            }), paneSub: formatCountSubtitle(formatMessage, dataHook.totalRecords, dataHook.isCountCapped), actionMenu: isCurrentUserAdmin
                ? function (_a) {
                    var onToggle = _a.onToggle;
                    return (React.createElement(MenuSection, { label: formatMessage({
                            id: "ui-br-legaldeposits.action.menu.label",
                        }), id: "users-actions-menu" },
                        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                                onToggle();
                                setShowCreateUserModal(true);
                            }, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.action.create" }))));
                }
                : undefined },
            React.createElement(TableComponent, { columnPrefsKey: "users", data: dataHook.usersData, columns: columns, pageSize: dataHook.pageSize, onPageSizeChange: dataHook.changePageSize, totalCount: dataHook.totalRecords, onNeedMoreData: function (offset) { return dataHook.fetchMoreData(offset); }, searchTerm: "", filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane, source: source, isFetching: dataHook.isFetching, resetPaginationKey: JSON.stringify(filtersHook.appliedFilters), onRowClick: actionsHook.handleRowClick })),
        actionsHook.selectedUser && (React.createElement(Pane, { id: "detailPane", defaultWidth: "35%", paneTitle: formatMessage({
                id: "ui-br-legaldeposits.users.detail.pane.title",
            }), onClose: actionsHook.handleCloseDetail, dismissible: true, actionMenu: isCurrentUserAdmin
                ? function (_a) {
                    var _b, _c;
                    var onToggle = _a.onToggle;
                    return (React.createElement(MenuSection, { label: formatMessage({
                            id: "ui-br-legaldeposits.action.menu.label",
                        }), id: "actions-menu" },
                        React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                                var _a;
                                onToggle();
                                if ((_a = actionsHook.selectedUser) === null || _a === void 0 ? void 0 : _a.id) {
                                    handleEditUser(actionsHook.selectedUser.id);
                                }
                            }, fullWidth: true },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.detail.action.edit" })),
                        ((_b = actionsHook.selectedUser) === null || _b === void 0 ? void 0 : _b.status) === "ACTIVE" && (React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () {
                                var _a;
                                onToggle();
                                if ((_a = actionsHook.selectedUser) === null || _a === void 0 ? void 0 : _a.id) {
                                    setStatusMutation.mutate({
                                        userId: actionsHook.selectedUser.id,
                                        status: "INACTIVE",
                                    });
                                }
                            } },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.detail.action.deactivate" }))),
                        ((_c = actionsHook.selectedUser) === null || _c === void 0 ? void 0 : _c.status) === "INACTIVE" && (React.createElement(Button, { buttonStyle: "dropdownItem", fullWidth: true, onClick: function () {
                                var _a;
                                onToggle();
                                if ((_a = actionsHook.selectedUser) === null || _a === void 0 ? void 0 : _a.id) {
                                    setStatusMutation.mutate({
                                        userId: actionsHook.selectedUser.id,
                                        status: "ACTIVE",
                                    });
                                }
                            } },
                            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.detail.action.activate" })))));
                }
                : undefined },
            React.createElement(UserDetail, { user: actionsHook.selectedUser }))),
        React.createElement(Modal, { open: showCreateUserModal, label: formatMessage({
                id: "ui-br-legaldeposits.users.modal.create.heading",
            }), onClose: function () { return setShowCreateUserModal(false); }, dismissible: true },
            React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.modal.create.warning" })),
            React.createElement(Layout, { className: "marginTop1" },
                React.createElement("p", null,
                    React.createElement("strong", null,
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.modal.create.instructions.title" }))),
                React.createElement(Layout, { className: "marginTopHalf" },
                    React.createElement(List, { items: [
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step1",
                            }),
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step2",
                            }),
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step3",
                            }),
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step4",
                            }),
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step5",
                            }),
                            formatMessage({
                                id: "ui-br-legaldeposits.users.modal.create.step6",
                            }),
                        ], itemFormatter: function (item, index) { return (React.createElement("li", { key: index }, item)); }, listStyle: "bullets", marginBottom0: true }))),
            React.createElement(Layout, { className: "display-flex marginTop1" },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () {
                        setShowCreateUserModal(false);
                        history.push(EXTERNAL_ROUTES.USERS_MODULE);
                    } },
                    React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.modal.create.goToUsers" })),
                React.createElement(Layout, { className: "margin-start-gutter" },
                    React.createElement(Button, { onClick: function () { return setShowCreateUserModal(false); } },
                        React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.users.modal.create.close" })))))));
};
export default UserManagementList;
